import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Check, CreditCard } from "lucide-react";
import { useNavigate } from "react-router-dom";

import {
  USER_TYPES,
  getActiveProducts,
  getActiveProductsByUserType,
} from "../../services/subscriptionProductService";

import PartnerHeader from "../../component/PartnerHeader";
import PartnerFooter from "../../component/PartnerFooter";
import "../Subscription/Subscription.css";

/**
 * Get logged-in user's role from localStorage.
 */
const getStoredRole = () => {
  try {
    const direct =
      localStorage.getItem("role") ||
      localStorage.getItem("userRole");

    if (direct) {
      return direct
        .replace(/"/g, "")
        .replace("ROLE_", "")
        .trim()
        .toUpperCase();
    }

    const userRaw = localStorage.getItem("user");

    if (userRaw) {
      const user = JSON.parse(userRaw);

      const role =
        user?.role ||
        user?.userType ||
        user?.user_type;

      if (role) {
        return String(role)
          .replace("ROLE_", "")
          .trim()
          .toUpperCase();
      }
    }
  } catch (error) {
    console.error("Unable to read user role:", error);
  }

  return "";
};

/**
 * Format validity days.
 *
 * 1 day       -> 1 Day
 * 30 days     -> 30 Days
 * 365 days    -> 1 Year
 */
const formatValidity = (days) => {
  const value = Number(days || 0);

  if (value === 1) return "1 Day";

  if (value === 365) return "1 Year";

  if (value > 365 && value % 365 === 0) {
    return `${value / 365} Years`;
  }

  if (value > 30 && value % 30 === 0) {
    return `${value / 30} Months`;
  }

  return `${value} Days`;
};

/**
 * Display unlimited when backend sends 0 or negative.
 *
 * If your business logic considers 0 as "no access"
 * instead of unlimited, change this function accordingly.
 */
const formatLimit = (value) => {
  const limit = Number(value ?? 0);

  if (limit < 0) return "Unlimited";

  return limit.toLocaleString("en-IN");
};

/**
 * Subscription Plans Component
 *
 * Backend fields:
 *
 * id
 * name
 * code
 * userType
 * featureCode
 * description
 * isActive
 * createdAt
 * updatedAt
 * price
 * resumeLimit
 * validityDays
 * jobPostLimit
 * jobInviteLimit
 * profileViewLimit
 */
function SubscriptionPlans({ mode }) {
  const navigate = useNavigate();
  const role = useMemo(() => getStoredRole(), []);

  const isAdmin = mode
    ? mode.toUpperCase() === "ADMIN"
    : role === "ADMIN";

  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [typeFilter, setTypeFilter] = useState("ALL");

  /**
   * Load subscription products from backend.
   */
  const loadPlans = useCallback(async () => {
    try {
      setLoading(true);
      setError("");

      let data = [];

      if (isAdmin) {
        /**
         * ADMIN:
         *
         * ALL
         * PARTNER
         * TRAINER
         * EMPLOYEE
         */
        if (typeFilter === "ALL") {
          data = await getActiveProducts();
        } else {
          data = await getActiveProductsByUserType(typeFilter);
        }
      } else if (role === "PARTNER") {
        /**
         * PARTNER
         */
        data = await getActiveProductsByUserType("PARTNER");
      } else if (USER_TYPES.includes(role)) {
        /**
         * TRAINER / EMPLOYEE
         */
        data = await getActiveProductsByUserType(role);
      } else {
        data = [];
      }

      setPlans(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Subscription plans error:", error);

      setPlans([]);

      setError(
        error?.message ||
          "Unable to load subscription plans."
      );
    } finally {
      setLoading(false);
    }
  }, [isAdmin, role, typeFilter]);

  useEffect(() => {
    loadPlans();
  }, [loadPlans]);

  /**
   * Choose plan.
   * Opens the checkout page for the selected subscription plan.
   */
  const choosePlan = (plan) => {
    if (!plan?.id) {
      console.error("Subscription plan ID is missing.", plan);
      return;
    }

    navigate(`/partner/subscriptions/checkout/${plan.id}`);
  };

  /**
   * Features directly from subscription_products table.
   */
  const getPlanLimits = (plan) => {
    return [
      {
        key: "resume",
        value: formatLimit(plan.resumeLimit),
        label: "Resume Downloads",
      },
      {
        key: "jobPost",
        value: formatLimit(plan.jobPostLimit),
        label: "Job Posts",
      },
      {
        key: "jobInvite",
        value: formatLimit(plan.jobInviteLimit),
        label: "Job Invites",
      },
      {
        key: "profileView",
        value: formatLimit(plan.profileViewLimit),
        label: "Profile Views",
      },
    ];
  };

  const content = (
    <div className="sp-page">

      {/* HEADER */}
      <header className="sp-head">
        <div>
          <span className="sp-kicker">
            Subscriptions
          </span>

          <h1>
            Subscription Plans
          </h1>

          <p>
            {isAdmin
              ? "Manage and view active subscription plans."
              : "Choose the subscription plan that fits your requirements."}
          </p>
        </div>
      </header>

      {/* ADMIN FILTER */}
      {isAdmin && (
        <div className="sp-toolbar">

          <div className="sp-tabs">

            {["ALL", ...USER_TYPES].map((type) => (
              <button
                key={type}
                type="button"
                className={
                  typeFilter === type
                    ? "active"
                    : ""
                }
                onClick={() =>
                  setTypeFilter(type)
                }
              >
                {type === "ALL"
                  ? "All"
                  : type.charAt(0) +
                    type.slice(1).toLowerCase()}
              </button>
            ))}

          </div>
        </div>
      )}

      {/* LOADING */}
      {loading ? (
        <div className="sp-state">

          <div className="sp-spinner" />

          <p>
            Loading subscription plans...
          </p>

        </div>

      ) : error ? (

        /* ERROR */
        <div className="sp-state">

          <h3>
            Something went wrong
          </h3>

          <p>
            {error}
          </p>

          <button
            type="button"
            className="sp-btn sp-btn-primary"
            onClick={loadPlans}
          >
            Try Again
          </button>

        </div>

      ) : plans.length === 0 ? (

        /* NO PLANS */
        <div className="sp-state">

          <CreditCard size={42} />

          <h3>
            No Plans Available
          </h3>

          <p>
            No active subscription plans are
            available for this user type.
          </p>

        </div>

      ) : (

        /* PLANS */
        <div className="sp-plans">

          {plans.map((plan) => (

            <article
              className="sp-plan"
              key={plan.id}
            >

              {/* USER TYPE */}
              <span className="sp-chip">
                {plan.userType || "PLAN"}
              </span>

              {/* ADMIN CODE */}
              {isAdmin && plan.code && (
                <code className="sp-code">
                  {plan.code}
                </code>
              )}

              {/* PLAN NAME */}
              <h3>
                {plan.name}
              </h3>

              {/* PRICE */}
              <div className="sp-plan-price">

                <strong>
                  ₹
                  {Number(
                    plan.price || 0
                  ).toLocaleString("en-IN")}
                </strong>

                <span>
                  / {formatValidity(plan.validityDays)}
                </span>

              </div>

              {/* DESCRIPTION */}
              {plan.description && (
                <p className="sp-plan-desc">
                  {plan.description}
                </p>
              )}

              {/* FEATURES */}
              <ul>

                {getPlanLimits(plan).map(
                  (limit) => (

                    <li key={limit.key}>

                      <Check size={15} />

                      <span>
                        <b>
                          {limit.value}
                        </b>{" "}
                        {limit.label}
                      </span>

                    </li>
                  )
                )}

                {/* VALIDITY */}
                <li>

                  <Check size={15} />

                  <span>
                    Valid for{" "}
                    <b>
                      {formatValidity(
                        plan.validityDays
                      )}
                    </b>
                  </span>

                </li>

                {/* FEATURE CODE */}
                {plan.featureCode && (
                  <li>

                    <Check size={15} />

                    <span>
                      Feature:{" "}
                      <b>
                        {plan.featureCode}
                      </b>
                    </span>

                  </li>
                )}

              </ul>

              {/* PURCHASE BUTTON */}
              {!isAdmin && (
                <button
                  type="button"
                  className="sp-btn sp-btn-primary"
                  onClick={() =>
                    choosePlan(plan)
                  }
                >
                  Choose Plan
                </button>
              )}

            </article>

          ))}

        </div>
      )}


    </div>
  );

  /**
   * ADMIN:
   * Admin already has its own layout/header.
   */
  if (isAdmin) {
    return content;
  }

  /**
   * PARTNER / TRAINER / EMPLOYEE
   */
  return (
    <>
      <PartnerHeader />

      {content}

      <PartnerFooter />
    </>
  );
}

export default SubscriptionPlans;